
import { mapGetters } from 'vuex';
import Overlay from '@/components/Overlay.vue';
import { INSERT_NOTE, UPDATE_ACTION_METADATA } from '@/gql';
import { actionForceCancel, actionForceOpen, actionUpdateChannelEndpoint } from '@/api/action';
import TranslationHelper from '@/helpers/translation';
import axios from 'axios';
import Contacts from '@/components/_Contacts/index.vue';
import * as CONSTANTS from '@/constants';
import LoadingButton from '@/components/_Buttons/LoadingButton.vue';
import { Risolto_Action_Status_Enum, Risolto_Channel_Enum, Risolto_Contact, Risolto_Note_Category_Enum } from '@/generated/graphql';
import { differenceInDays } from 'date-fns';
import Logging from '@/logging';

export default {
	components: {
		Overlay,
		Contacts,
		LoadingButton
	},
	props: {
		visible: { required: true, type: Boolean },
		action: { required: false, type: String, default: undefined }
	},
	data() {
		return {
			resultCategories: undefined,
			isDirty: false,
			loading: false,
			navigating: false,
			fetchingAction: false,
			localAction: null,
			form: {
				reason: '',
				callResult: undefined,
				callResultDescription: ''
			},
			rules: {
				callResult: [{ required: true, message: this.$t('fieldRequired') }]
			}
		};
	},
	computed: {
		...mapGetters(['beta', 'selectedInvoices', 'selectedParticipantStore', 'loggedInUser']),
		...mapGetters('worklist', {
			queueActive: 'actionQueueActive',
			queuePosition: 'actionQueuePosition',
			queueLength: 'actionQueueLength',
			queueHasNext: 'actionQueueHasNext',
			queueHasPrevious: 'actionQueueHasPrevious'
		}),
		recipientNameAndAccountId() {
			const recipient = this.localAction?.recipient_by_reference;
			if (!recipient?.name) {
				return '';
			}
			return recipient.account_id ? `${recipient.name} (${recipient.account_id})` : recipient.name;
		},
		CONSTANTS() {
			return CONSTANTS;
		},
		recipient() {
			return this.localAction?.recipient_by_reference;
		}
	},
	watch: {
		async action(newAction) {
			if (!newAction) {
				this.localAction = null;
				return;
			}
			// Skip refetch if navigateNext already loaded this action
			if (this.localAction?.reference === newAction) return;

			this.fetchingAction = true;
			this.localAction = null;
			try {
				const fetchedAction = await this.$store.dispatch('actions/fetchActionDetails', newAction);
				this.localAction = fetchedAction;
			} catch (e) {
				Logging.error('Error fetching action details:', e);
				this.$notify({
					title: this.$t('somethingWentWrong'),
					message: this.$t('somethingWentWrong'),
					type: 'error'
				});
			} finally {
				this.fetchingAction = false;
			}
		},
		localAction() {
			this.loading = false;
			this.form = {
				reason: '',
				callResult: undefined,
				callResultDescription: ''
			};
			const channel = this.localAction?.metadata?.channels?.[0];
			if (channel) {
				this.form.reason = channel.reason || channel.reason_for_call?.default_message || '';
			}
			// Restore call result from note for executed actions
			const note = this.localAction?.notes?.[0];
			if (note) {
				this.form.callResult = note.category || undefined;
				this.form.callResultDescription = note.note || '';
			}
		}
	},
	created() {
		this.resultCategories = Risolto_Note_Category_Enum;
	},
	methods: {
		preSelectedContacts(contact) {
			const endpoints = contact.active_endpoints?.filter((endpoint) => {
				return endpoint.reference === this.localAction?.channel_endpoint;
			});
			if (endpoints && endpoints.length > 0) {
				return true;
			}

			return false;
		},
		disabledContacts(contact) {
			const endpoints = contact.active_endpoints?.filter((endpoint) => {
				return endpoint.channel === Risolto_Channel_Enum.Phone;
			});
			if (!endpoints || endpoints.length === 0) {
				return true;
			}

			return false;
		},
		handleSelectContact(contact: Risolto_Contact | null) {
			if (this.disabledContacts && this.disabledContacts(contact)) {
				this.$notify({
					title: this.$t('warning'),
					message: this.$t('contact_no_valid_channel_endpoint'),
					type: 'warning'
				});
				return;
			}
			this.$confirm(this.$t('Contacts.confirm_select_contact'), {
				confirmButtonText: this.$t('Ok'),
				cancelButtonText: this.$t('cancel'),
				type: 'warning'
			}).then(() => {
				this.$store.commit('risolto/SET_SELECTED_CONTACT', contact);
			}).catch(() => {
				// do nothing
			});
		},
		canExecute(action) {
			if (!action) {
				return false;
			}

			if (action.status === 'OPEN') {
				return true;
			}

			if (action.status === 'DRAFT') {
				const today = new Date(Date.now());
				const dueDate = new Date(action.due_date);

				const diffInDays = differenceInDays(today, dueDate);

				return diffInDays <= 3;
			}

			return false;
		},
		handleOverlayClose() {
			const wasInQueue = this.queueActive;
			this.$store.dispatch('risolto/setActionPhoneDetailOverlayVisible', false);
			this.$store.commit('worklist/CLEAR_ACTION_QUEUE');
			if (wasInQueue) {
				this.$router.push({
					name: 'worklist',
					params: { participant: this.selectedParticipantStore }
				});
				this.$store.commit('risolto/SET_ACTION_TABLE_VISIBLE', true);
			}
		},
		async navigateToIndex(index) {
			const item = this.$store.state.worklist.actionQueue[index];
			if (!item || this.navigating) return;
			this.navigating = true;

			this.$store.commit('worklist/SET_ACTION_QUEUE_INDEX', index);
			this.$store.commit('actions/SET_SELECTED_ACTION_REF', item.ref);

			try {
				const action = await this.$store.dispatch('actions/fetchActionDetails', item.ref);
				if (!action || action.channel !== 'PHONE') return;

				const previousRecipient = this.localAction?.recipient;
				this.localAction = action;
				if (action.recipient !== previousRecipient) {
					this.$router.push({
						name: 'recipient-detail',
						params: { id: action.recipient, participant: action.participant }
					});
				}
			} finally {
				this.navigating = false;
			}
		},
		navigateNext() {
			if (!this.queueHasNext) return;
			this.navigateToIndex(this.$store.state.worklist.actionQueueIndex + 1);
		},
		translateNoteCategory(cat) {
			return TranslationHelper.note_category(this, cat);
		},
		async onCancelAction() {
			this.loading = true;

			const payload = {
				version: 'v1',
				participant: this.localAction.participant,
				actions: [this.localAction.reference],
				reason: this.form['callResultDescription'] ? this.form['callResultDescription'] : ''
			};

			try {
				await axios.post(`https://${process.env.VUE_APP_API_ENDPOINT}/dunning/v1/action/cancel`, payload, {
					headers: { Authorization: `Bearer ${localStorage.getItem('rslt_id_token')}` }
				});

				this.localAction.status = 'CANCELED';
				this.loading = false;

				this.$store.commit('apollo/RISOLTO_ACTION');
				this.$notify({
					title: this.$t('success'),
					message: this.$tc('actionCanceled', 1),
					type: 'success'
				});
			} catch (e) {
				this.loading = false;
				Logging.error(e);
			}
		},
		async handleResolve() {
			this.$refs['form'].validate(async (valid) => {
				if (valid) {
					await this.resolve();
				} else {
					this.loading = false;
					this.$notify({
						title: this.$t('warning'),
						message: this.$t('fillInFields'),
						type: 'warning'
					});
					return false;
				}
			});
		},
		async resolve() {
			if (this.loading) {
				return;
			}
			this.loading = true;
			Logging.log('has action', this.localAction);
			const contact: Risolto_Contact | null = this.$store.getters.selectedContact;
			if (contact != null) {
				const phoneChannelEndpoints = contact.channel_endpoints.filter((channel) => {
					return channel.channel === Risolto_Channel_Enum.Phone;
				});
				if (phoneChannelEndpoints.length > 0) {
					actionUpdateChannelEndpoint(this.localAction.reference, phoneChannelEndpoints[0].reference);
				}
			}

			let newMetadata = this.localAction.metadata;
			let newNote = {
				note: this.form['callResultDescription'] ? this.form['callResultDescription'] : '',
				category: this.form['callResult'] ? this.form['callResult'] : undefined,
				participant: this.localAction.participant,
				legal_entity: this.localAction.legal_entity,
				recipient: this.localAction.recipient,
				created_by: this.loggedInUser.reference,
				action: this.localAction.reference
			};
			Logging.log('newMetadata: ', newMetadata);

			try {
				await this.$apollo.mutate({
					mutation: INSERT_NOTE,
					variables: newNote
				});

				let updateActionMetadataResult = await this.$apollo.mutate({
					mutation: UPDATE_ACTION_METADATA,
					variables: {
						action: this.localAction.reference,
						channel: this.localAction.channel,
						metadata: newMetadata
					}
				});

				Logging.log('Result of updating Action Metadata: ', updateActionMetadataResult);

				if (updateActionMetadataResult.data.update_risolto_action.updated[0].reference) {
					if (this.localAction.status === 'DRAFT') {
						await actionForceOpen(this.selectedParticipantStore, [this.localAction]);
					}
					const actionToConfirm = updateActionMetadataResult.data.update_risolto_action.updated[0].reference;

					const payload = {
						version: 'v1',
						participant: this.localAction.participant,
						actions: [actionToConfirm],
						dry_run: false,
						force_send: this.$store.getters['forceSendStore']
					};

					Logging.log('payload: ', payload);

					await axios.post(`https://${process.env.VUE_APP_API_ENDPOINT}/dunning/v1/action/confirm`, payload, {
						headers: { Authorization: `Bearer ${localStorage.getItem('rslt_id_token')}` }
					});

					// Optimistic update — mutate in-place to keep form state intact
					this.localAction.status = 'DONE';
					this.loading = false;

					this.$store.commit('apollo/RISOLTO_ACTION');
					this.$notify({ title: this.$tc('actionExecuted', 1), type: 'success' });
				} else {
					this.$notify({
						title: this.$t('somethingWentWrong'),
						message: 'Error in update',
						type: 'error'
					});
				}
			} catch (e) {
				Logging.error(e);
			}
		},
		async forceOpenAction() {
			this.$confirm(this.$t('can_not_be_undone'), 'Warning', {
				confirmButtonText: this.$t('Ok'),
				cancelButtonText: this.$t('cancel')
			}).then(async () => {
				try {
					await actionForceOpen(
						this.selectedParticipantStore,
						[this.localAction]
					);

					this.localAction.status = Risolto_Action_Status_Enum.Open;

					this.$notify({
						title: this.$tc('actionOpened', 1),
						type: 'success'
					});
				} catch (e) {
					this.$notify({
						title: this.$t('somethingWentWrong'),
						message: this.$t('somethingWentWrong'),
						type: 'error'
					});
				}
			}).catch(() => {}); // User cancelled — do nothing
		},
		async forceCancelAction() {
			this.$confirm(this.$t('can_not_be_undone'), 'Warning', {
				confirmButtonText: this.$t('Ok'),
				cancelButtonText: this.$t('cancel')
			}).then(async () => {
				try {
					await actionForceCancel(
						this.selectedParticipantStore,
						[this.localAction]
					);

					this.localAction.status = Risolto_Action_Status_Enum.Canceled;

					this.$notify({
						title: this.$tc('actionCanceled', 1),
						type: 'success'
					});
				} catch (e) {
					this.$notify({
						title: this.$t('somethingWentWrong'),
						message: this.$t('somethingWentWrong'),
						type: 'error'
					});
				}
			}).catch(() => {}); // User cancelled — do nothing
		}
	}
};
